/**
 * Der einzige Ort, an dem `fetch` steht.
 *
 * Die Rollenteilung: Hier liegt der Transport — Basis-URL, Bearer-Token,
 * Idempotenzschlüssel, die Übersetzung des einen Fehlerformats. Die typisierten
 * Aufrufe je Fachbereich liegen in `features/<bereich>/api.ts` und benutzen
 * `request()`. In einer Komponente steht weder das eine noch das andere.
 *
 * Das Fehlerformat der API ist festgelegt (ARCHITECTURE.md 5):
 *
 *     {"error": {"code": "...", "message": "...", "field": "..."}}
 *
 * Beide Clients zeigen Feldfehler an derselben Stelle an, ohne Texte zu parsen.
 * Deshalb wird `field` durchgereicht und nicht verworfen.
 */

/** Basis ist relativ: In der Entwicklung greift der Vite-Proxy, in prod liegt
 * das Bundle im selben Container wie die API — gleiche Herkunft (ADR 0005). */
const BASE = '/api/v1'

export class ApiError extends Error {
  // Felder ausgeschrieben statt als Konstruktor-Parameter: `erasableSyntaxOnly`
  // erlaubt nur Syntax, die sich durch Löschen in gültiges JavaScript
  // verwandelt — Parameter-Properties erzeugen dagegen Code.
  readonly status: number
  readonly code: string
  readonly field: string | null

  constructor(status: number, code: string, message: string, field: string | null) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.field = field
  }

  /** Fehlender Grant oder zu niedrige Rolle. Nicht dasselbe wie „nicht da". */
  get isForbidden(): boolean {
    return this.status === 403
  }

  /** Token fehlt oder ist abgelaufen — die Oberfläche schickt zur Anmeldung. */
  get isUnauthenticated(): boolean {
    return this.status === 401
  }
}

/** Die Antwort gehört zu einer inzwischen beendeten Sitzung. */
export class SessionChangedError extends Error {
  constructor() {
    super('Die Sitzung hat sich während der Anfrage geändert.')
    this.name = 'SessionChangedError'
  }
}

/** Liefert das aktuelle Zugangstoken oder `null`. Wird von der Anmeldung
 * gesetzt; hier bewusst als Funktion, damit dieses Modul nichts über Supabase
 * wissen muss. */
type TokenSource = () => string | null
type SessionGenerationSource = () => number
/** Spec 25, Fachregel 5: „Beide Clients reagieren auf 403 `user_disabled`" —
 * dieselbe Brücke wie `TokenSource`, damit dieses Modul weiterhin nichts über
 * Supabase weiß. `features/auth/AuthProvider.tsx` trägt die echte Abmeldung
 * ein. */
type SignOutSource = () => void

let readToken: TokenSource = () => null
let readSessionGeneration: SessionGenerationSource = () => 0
let requestSignOut: SignOutSource = () => undefined

export function setTokenSource(source: TokenSource): void {
  readToken = source
}

/** Die Anmeldung erhöht die Generation nur für eine neue Identität. */
export function setSessionGenerationSource(source: SessionGenerationSource): void {
  readSessionGeneration = source
}

export function setSignOutSource(source: SignOutSource): void {
  requestSignOut = source
}

export interface RequestOptions {
  readonly method?: 'GET' | 'POST' | 'PATCH' | 'DELETE'
  readonly body?: unknown
  readonly signal?: AbortSignal
  /** Schreibende Aufrufe tragen ihn, damit dieselbe Zahlung bei schlechtem
   * Empfang nicht zweimal ankommt. Der Server merkt sich den Schlüssel 24 h. */
  readonly idempotencyKey?: string
  /** Optimistisches Sperren bei Verträgen, Tickets und Objekten. */
  readonly ifMatch?: string
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers = new Headers({ Accept: 'application/json' })

  const token = readToken()
  const sessionGeneration = readSessionGeneration()
  if (token !== null) headers.set('Authorization', `Bearer ${token}`)
  if (options.body !== undefined) headers.set('Content-Type', 'application/json')
  if (options.idempotencyKey !== undefined) {
    headers.set('Idempotency-Key', options.idempotencyKey)
  }
  if (options.ifMatch !== undefined) headers.set('If-Match', options.ifMatch)

  try {
    const response = await fetch(`${BASE}${path}`, {
      method: options.method ?? 'GET',
      headers,
      ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
      ...(options.signal !== undefined ? { signal: options.signal } : {}),
    })

    if (!response.ok) {
      const apiError = await toApiError(response)
      // Gesperrt wirkt sofort: Der Gesperrte meldet sich selbst ab, statt auf
      // den nächsten Start zu warten (Spec 25, Fachregel 5) — dasselbe Bild
      // wie ein abgelaufenes Token, nur ausgelöst vom Server statt vom Ablauf.
      if (apiError.code === 'user_disabled') requestSignOut()
      throw apiError
    }
    if (response.status === 204) return undefined as T

    return (await response.json()) as T
  } finally {
    assertCurrentSession(sessionGeneration)
  }
}

function assertCurrentSession(sessionGeneration: number): void {
  if (readSessionGeneration() !== sessionGeneration) throw new SessionChangedError()
}

async function toApiError(response: Response): Promise<ApiError> {
  // Ein Fehler ohne verwertbaren Rumpf ist der Normalfall bei 502 und 504 —
  // dann steht wenigstens der Status da, statt dass das Parsen selbst wirft
  // und die eigentliche Ursache verdeckt.
  const fallback = `Die Anfrage ist fehlgeschlagen (${String(response.status)}).`

  try {
    const body: unknown = await response.json()
    const error = (body as { error?: { code?: string; message?: string; field?: string | null } })
      .error
    if (error !== undefined) {
      return new ApiError(
        response.status,
        error.code ?? 'unknown',
        error.message ?? fallback,
        error.field ?? null,
      )
    }
  } catch {
    // Kein JSON im Rumpf — unten mit dem Status weitermachen.
  }

  return new ApiError(response.status, 'unknown', fallback, null)
}
